'use client';

import H2 from '@/components/typography/h2';
import H3 from '@/components/typography/h3';
import Label from '@/components/typography/label';
import P from '@/components/typography/p';
import { Button } from '@/components/ui/button';
import { CopiableEmail } from '@/components/ui/copiable';
import { RichText } from '@/components/ui/rich-text';
import { Vacature } from '@/payload-types';
import {
    SerializedEditorState,
    SerializedLexicalNode,
} from '@payloadcms/richtext-lexical/lexical';
import { Dot } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type CardScrollVacancyProps = {
    vacancy: Vacature;
};

export default function CardScroll({ vacancy }: CardScrollVacancyProps) {
    const { t } = useTranslation();

    const getRichText = (key: keyof Vacature) => {
        const value = vacancy[key];
        if (typeof value === 'object' && value !== null) {
            return (
                <RichText
                    data={value as SerializedEditorState<SerializedLexicalNode>}
                />
            );
        }
        return null;
    };

    const wildcardContent = getRichText('wildcard');
    const wildcardContentIsValid =
        !!wildcardContent?.props?.data?.root?.children?.[0]?.children?.length;

    const sections = useMemo(() => {
        const baseSections: Array<{
            title?: string;
            key?: keyof Vacature | 'cta';
            content?: React.ReactNode;
        }> = [
            {
                content: (
                    <div className="relative flex justify-start">
                        <div className="flex flex-col gap-5">
                            <div>
                                <Label>{t('title')}</Label>
                            </div>
                            <H2>{vacancy.titel}</H2>
                            <div className="flex gap-5">
                                <div className="border-primary rounded-full border px-3 py-1">
                                    <div className="flex items-center gap-2">
                                        <Dot className="bg-primary h-2.5 w-2.5 rounded-full border lg:h-3.5 lg:w-3.5" />
                                        <Label>{vacancy.level}</Label>
                                    </div>
                                </div>
                                <div className="border-primary rounded-full border px-3 py-1">
                                    <div className="flex items-center gap-2">
                                        <Dot className="bg-primary h-2.5 w-2.5 rounded-full border lg:h-3.5 lg:w-3.5" />
                                        <Label>{vacancy.uren}</Label>
                                    </div>
                                </div>
                            </div>
                            <H3>{vacancy['pay-off']}</H3>
                        </div>

                        <div className="pointer-events-none absolute -right-20 -bottom-[12rem]">
                            <Image
                                src="/images/stickers/coffe.png"
                                alt="coffe"
                                height={250}
                                width={250}
                            />
                        </div>
                    </div>
                ),
            },
            {
                title: t('cardScroll.about.title'),
                content: <P>{t('cardScroll.about.text')}</P>,
            },
            { title: t('cardScroll.role'), key: 'rol' },
            { title: t('cardScroll.profile'), key: 'profiel' },
            { title: t('cardScroll.offering'), key: 'aanbod' },
        ];

        if (wildcardContentIsValid) {
            baseSections.push({ title: 'Extra', key: 'wildcard' });
        }

        baseSections.push({ title: 'cta', key: 'cta' });
        return baseSections;
    }, [vacancy, wildcardContentIsValid, t]);

    return (
        <div className="bg-beige border-primary border-b">
            <div>
                {sections.map((section, index) => (
                    <section
                        key={index}
                        className={`border-primary w-full px-5 py-15 ${
                            index === 0 ? 'border-none' : 'border-t'
                        }`}
                    >
                        {section.key === 'cta' ? (
                            <div className="flex flex-col gap-15">
                                <H2 className="text-center">
                                    {t('cardScroll.cta')}
                                    <CopiableEmail email="merle@foodcabinet.nl" />
                                    {t('cardScroll.cta2')}
                                </H2>

                                {vacancy.isExterneVacature && (
                                    <Link
                                        target="_blank"
                                        className="mx-auto w-fit"
                                        href={
                                            vacancy.externeVacatureLink as string
                                        }
                                    >
                                        <Button className="px-6 py-5">
                                            <Label variant="button">
                                                {t('cardScroll.apply')}
                                            </Label>
                                        </Button>
                                    </Link>
                                )}
                            </div>
                        ) : (
                            <div className="relative flex w-full flex-col gap-5">
                                {section.title && <H3>{section.title}</H3>}
                                {section.content ? (
                                    section.content
                                ) : (
                                    <P className="relative">
                                        {getRichText(
                                            section.key as keyof Vacature,
                                        )}
                                        {section.key === 'aanbod' && (
                                            <Image
                                                src="/images/stickers/goodfood.png"
                                                alt="aanbod"
                                                height={125}
                                                width={200}
                                                className="absolute -top-32 -right-12 hidden"
                                            />
                                        )}
                                    </P>
                                )}
                            </div>
                        )}
                    </section>
                ))}
            </div>
        </div>
    );
}
